import { useState, useEffect, useRef } from 'react'
import { FileText, Plus, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Send, Check, Ban, Trash2, Calendar } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useQuotes, useQuote, useAddQuote,
  useUpdateQuote,
  useAddQuoteItem, useDeleteQuoteItem, useDeleteQuote,
  useSendQuote, useApproveQuote, useRejectQuote,
  useVehicles, useParts,
} from '../api/hooks'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const STATUS = {
  draft:    { label: 'Draft',    cls: 'bg-gray-500/15 text-gray-400' },
  sent:     { label: 'Sent',     cls: 'bg-blue-500/15 text-blue-400' },
  viewed:   { label: 'Viewed',   cls: 'bg-indigo-500/15 text-indigo-400' },
  approved: { label: 'Approved', cls: 'bg-green-500/15 text-green-400' },
  rejected: { label: 'Rejected', cls: 'bg-red-500/15 text-red-400' },
  expired:  { label: 'Expired',  cls: 'bg-orange-500/15 text-orange-400' },
}

const fmtGBP  = (v) => `£${Number(v || 0).toFixed(2)}`
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—'

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
const WEEK_DAYS = ['Mo','Tu','We','Th','Fr','Sa','Su']

// ─── Calendar picker ──────────────────────────────────────────────────────────

function CalendarPicker({ value, onChange, placeholder = 'Select date' }) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState(() => {
    const d = value ? new Date(value + 'T00:00:00') : new Date()
    return { year: d.getFullYear(), month: d.getMonth() }
  })
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  useEffect(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00')
      setView({ year: d.getFullYear(), month: d.getMonth() })
    }
  }, [value])

  const daysInMonth  = new Date(view.year, view.month + 1, 0).getDate()
  const firstDayMon  = (new Date(view.year, view.month, 1).getDay() + 6) % 7

  const prevMonth = () => setView(v => ({
    year:  v.month === 0 ? v.year - 1 : v.year,
    month: v.month === 0 ? 11 : v.month - 1,
  }))
  const nextMonth = () => setView(v => ({
    year:  v.month === 11 ? v.year + 1 : v.year,
    month: v.month === 11 ? 0 : v.month + 1,
  }))

  const select = (day) => {
    const pad = n => String(n).padStart(2, '0')
    onChange(`${view.year}-${pad(view.month + 1)}-${pad(day)}`)
    setOpen(false)
  }

  const displayValue = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-GB') : ''

  const today = new Date()
  const todayDay = today.getMonth() === view.month && today.getFullYear() === view.year ? today.getDate() : null
  const selectedDay = value
    ? (() => { const d = new Date(value + 'T00:00:00'); return d.getMonth() === view.month && d.getFullYear() === view.year ? d.getDate() : null })()
    : null

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={clsx(inputCls, 'flex items-center justify-between text-left cursor-pointer')}
      >
        <span className={displayValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}>
          {displayValue || placeholder}
        </span>
        <Calendar size={15} className="text-[var(--text-muted)] shrink-0 ml-2" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 p-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl w-64">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth}
              className="p-1 rounded hover:bg-[var(--bg-overlay)] text-[var(--text-secondary)] transition-colors">
              <ChevronLeft size={15} />
            </button>
            <span className="text-sm font-semibold text-[var(--text-primary)]">
              {MONTHS[view.month]} {view.year}
            </span>
            <button type="button" onClick={nextMonth}
              className="p-1 rounded hover:bg-[var(--bg-overlay)] text-[var(--text-secondary)] transition-colors">
              <ChevronRight size={15} />
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {WEEK_DAYS.map(d => (
              <div key={d} className="text-center text-xs font-medium text-[var(--text-muted)] py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {[...Array(firstDayMon)].map((_, i) => <div key={`e${i}`} />)}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1
              const isSelected = day === selectedDay
              const isToday    = day === todayDay
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => select(day)}
                  className={clsx(
                    'w-full aspect-square flex items-center justify-center text-xs rounded-lg transition-colors',
                    isSelected
                      ? 'bg-indigo-600 text-white font-semibold'
                      : isToday
                        ? 'bg-[var(--bg-overlay)] text-indigo-400 font-medium'
                        : 'text-[var(--text-primary)] hover:bg-[var(--bg-overlay)]'
                  )}
                >
                  {day}
                </button>
              )
            })}
          </div>

          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false) }}
              className="mt-2 w-full py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]
                         bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)] rounded-lg transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, cls: 'bg-gray-500/15 text-gray-400' }
  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', s.cls)}>
      {s.label}
    </span>
  )
}

// ─── Create Quote modal ───────────────────────────────────────────────────────

function CreateQuoteModal({ onClose }) {
  const addQuote  = useAddQuote()
  const { data: vehicles = [], isLoading: vLoading } = useVehicles()

  const defaultValidUntil = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 28)
    return d.toISOString().slice(0, 10)
  })()

  const [form, setForm] = useState({
    vehicleId:     '',
    validUntil:    defaultValidUntil,
    notes:         '',
    customerNotes: '',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.vehicleId) return toast.error('Please select a vehicle')
    try {
      await addQuote.mutateAsync({
        vehicleId:     form.vehicleId,
        validUntil:    form.validUntil   || null,
        notes:         form.notes        || null,
        customerNotes: form.customerNotes || null,
      })
      toast.success('Quote created')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create quote')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">New Quote</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Vehicle</label>
            <select value={form.vehicleId} onChange={set('vehicleId')} className={inputCls} required>
              <option value="">
                {vLoading ? 'Loading…' : vehicles.length ? 'Select vehicle' : 'No vehicles linked'}
              </option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>
                  {v.registration} — {v.make} {v.model}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Valid until</label>
            <CalendarPicker
              value={form.validUntil}
              onChange={v => setForm(p => ({ ...p, validUntil: v }))}
              placeholder="Pick a date"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Internal notes <span className="text-[var(--text-muted)]">(garage only)</span>
            </label>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Customer notes <span className="text-[var(--text-muted)]">(visible to customer)</span>
            </label>
            <textarea rows={2} value={form.customerNotes} onChange={set('customerNotes')}
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addQuote.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addQuote.isPending ? 'Creating…' : 'Create Quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Parts combobox ───────────────────────────────────────────────────────────

function PartsCombobox({ garageId, vehicleId, selectedPart, onSelect, description, onDescriptionChange }) {
  const { data: parts = [] } = useParts({ garageId, vehicleId })
  const [open,   setOpen]   = useState(false)
  const [search, setSearch] = useState('')
  const wrapRef             = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = parts.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.part_number && p.part_number.toLowerCase().includes(search.toLowerCase()))
  )

  const handleSelect = (part) => {
    onSelect(part)
    setSearch('')
    setOpen(false)
  }

  const handleClear = () => {
    onSelect(null)
    setSearch('')
  }

  if (selectedPart) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-2 text-sm bg-[var(--bg-base)] border border-indigo-500
                      rounded-lg text-[var(--text-primary)] min-w-0">
        <span className="truncate flex-1">{selectedPart.name}</span>
        {selectedPart.vehicle_registration && (
          <span className="text-xs text-indigo-400 shrink-0">{selectedPart.vehicle_registration}</span>
        )}
        {selectedPart.stock_quantity > 0 ? (
          <span className="text-xs text-green-400 shrink-0">{selectedPart.stock_quantity} in stock</span>
        ) : (
          <span className="text-xs text-orange-400 shrink-0">Out of stock</span>
        )}
        <button type="button" onClick={handleClear}
          className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] ml-1">
          <X size={12} />
        </button>
      </div>
    )
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        className={inputCls}
        placeholder="Search catalogue or type description…"
        value={open ? search : description}
        onChange={e => {
          if (open) setSearch(e.target.value)
          else onDescriptionChange(e.target.value)
        }}
        onFocus={() => { setSearch(''); setOpen(true) }}
      />
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 max-h-48 overflow-y-auto
                        bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-[var(--text-muted)]">
              No catalogue parts match — item will be added as free text.
            </p>
          ) : (
            filtered.map(p => (
              <button
                key={p.id}
                type="button"
                onMouseDown={() => handleSelect(p)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm
                           hover:bg-[var(--bg-overlay)] transition-colors text-left"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--text-primary)] truncate">{p.name}</span>
                    {p.for_this_vehicle && (
                      <span className="text-xs px-1.5 py-0.5 bg-indigo-500/15 text-indigo-400 rounded-full shrink-0">
                        This vehicle
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {p.part_number && (
                      <span className="text-xs text-[var(--text-muted)]">{p.part_number}</span>
                    )}
                    {p.vehicle_registration && !p.for_this_vehicle && (
                      <span className="text-xs text-[var(--text-muted)]">{p.vehicle_registration}</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 ml-3 text-right">
                  {p.stock_quantity > 0 ? (
                    <span className="text-xs font-medium text-green-400">{p.stock_quantity} in stock</span>
                  ) : (
                    <span className="text-xs text-orange-400">Out of stock</span>
                  )}
                  {p.unit_cost > 0 && (
                    <span className="block text-xs text-[var(--text-muted)]">
                      £{Number(p.unit_cost).toFixed(2)}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ─── Add item form (inline inside detail modal) ───────────────────────────────

function AddItemForm({ quoteId, garageId, vehicleId, onAdded }) {
  const addItem = useAddQuoteItem()
  const [type,         setType]         = useState('labour')
  const [description,  setDescription]  = useState('')
  const [quantity,     setQuantity]     = useState('1')
  const [unitPrice,    setUnitPrice]    = useState('0.00')
  const [vatRate,      setVatRate]      = useState('20')
  const [selectedPart, setSelectedPart] = useState(null)

  const handleSelectPart = (part) => {
    setSelectedPart(part)
    if (part) {
      setDescription(part.name)
      setUnitPrice(Number(part.unit_cost).toFixed(2))
    }
  }

  const handleTypeChange = (e) => {
    setType(e.target.value)
    setSelectedPart(null)
    setDescription('')
  }

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!description.trim()) return toast.error('Description required')
    try {
      await addItem.mutateAsync({
        quoteId,
        description,
        type,
        quantity:  parseFloat(quantity)  || 1,
        unitPrice: parseFloat(unitPrice) || 0,
        vatRate:   parseFloat(vatRate)   || 20,
        partId:    selectedPart?.id ?? null,
      })
      setDescription('')
      setQuantity('1')
      setUnitPrice('0.00')
      setVatRate('20')
      setSelectedPart(null)
      onAdded?.()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add item')
    }
  }

  const selectCls = clsx(inputCls, 'cursor-pointer')

  return (
    <form onSubmit={handleAdd} className="mt-3 p-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg space-y-2">
      <p className="text-xs font-medium text-[var(--text-secondary)]">Add line item</p>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-5">
          {type === 'part' ? (
            <PartsCombobox
              garageId={garageId}
              vehicleId={vehicleId}
              selectedPart={selectedPart}
              onSelect={handleSelectPart}
              description={description}
              onDescriptionChange={setDescription}
            />
          ) : (
            <input
              className={inputCls}
              placeholder="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              required
            />
          )}
        </div>
        <select value={type} onChange={handleTypeChange} className={clsx(selectCls, 'col-span-2')}>
          <option value="labour">Labour</option>
          <option value="part">Part</option>
          <option value="misc">Misc</option>
        </select>
        <input
          type="number" min="0" step="0.01"
          className={clsx(inputCls, 'col-span-2')}
          placeholder="Qty"
          value={quantity}
          onChange={e => setQuantity(e.target.value)}
        />
        <input
          type="number" min="0" step="0.01"
          className={clsx(inputCls, 'col-span-2')}
          placeholder="Unit £"
          value={unitPrice}
          onChange={e => setUnitPrice(e.target.value)}
        />
        <button
          type="submit"
          disabled={addItem.isPending}
          className="col-span-1 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700
                     disabled:opacity-50 text-white rounded-lg transition-colors"
        >
          <Plus size={16} />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-[var(--text-muted)]">VAT %</label>
        <input
          type="number" min="0" step="0.1"
          className={clsx(inputCls, 'w-20')}
          value={vatRate}
          onChange={e => setVatRate(e.target.value)}
        />
        {type === 'part' && selectedPart && (
          <span className="text-xs text-[var(--text-muted)] ml-2">
            VAT included in price for parts (20% standard)
          </span>
        )}
      </div>
    </form>
  )
}

// ─── Quote detail modal ───────────────────────────────────────────────────────

function QuoteDetailModal({ quoteId, isGarageView, canAdmin, onClose }) {
  const { user }       = useAuth()
  const { data: quote, isLoading } = useQuote(quoteId)
  const updateQuote    = useUpdateQuote()
  const deleteItem     = useDeleteQuoteItem()
  const deleteQuote    = useDeleteQuote()
  const sendQuote      = useSendQuote()
  const approveQuote   = useApproveQuote()
  const rejectQuote    = useRejectQuote()

  const [rejecting, setRejecting] = useState(false)
  const [reason,    setReason]    = useState('')
  const [meta, setMeta] = useState({ validUntil: '', notes: '', customerNotes: '' })

  useEffect(() => {
    if (quote) {
      setMeta({
        title:         quote.title || '',
        validUntil:    quote.valid_until ? quote.valid_until.slice(0, 10) : '',
        notes:         quote.notes || '',
        customerNotes: quote.customer_notes || '',
      })
    }
  }, [quote?.id])

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="text-[var(--text-secondary)] text-sm">Loading…</div>
      </div>
    )
  }
  if (!quote) return null

  const isDraft     = quote.status === 'draft'
  const isActionable = ['sent', 'viewed'].includes(quote.status)

  const handleSend = async () => {
    if (!window.confirm('Send this quote to the customer?')) return
    try {
      await sendQuote.mutateAsync(quote.id)
      toast.success('Quote sent')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send quote')
    }
  }

  const handleApprove = async () => {
    if (!window.confirm('Approve this quote?')) return
    try {
      await approveQuote.mutateAsync(quote.id)
      toast.success('Quote approved')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve quote')
    }
  }

  const handleReject = async () => {
    try {
      await rejectQuote.mutateAsync({ id: quote.id, reason: reason.trim() || undefined })
      toast.success('Quote rejected')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject quote')
    }
  }

  const handleSave = async () => {
    try {
      await updateQuote.mutateAsync({
        id:            quote.id,
        title:         meta.title,
        validUntil:    meta.validUntil || null,
        notes:         meta.notes,
        customerNotes: meta.customerNotes,
      })
      toast.success('Quote saved')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save quote')
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this draft quote? This cannot be undone.')) return
    try {
      await deleteQuote.mutateAsync(quote.id)
      toast.success('Quote deleted')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete quote')
    }
  }

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteItem.mutateAsync({ quoteId: quote.id, itemId })
      toast.success('Item removed')
    } catch {
      toast.error('Failed to remove item')
    }
  }

  const items    = quote.items || []
  const totals   = { subtotal: quote.subtotal, vat_amount: quote.vat_amount, total: quote.total }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-2xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
                  {quote.quote_number}
                </h2>
                <StatusBadge status={quote.status} />
              </div>
              {(quote.title || (isGarageView && isDraft)) && (
                isGarageView && isDraft ? null : (
                  <p className="text-sm text-[var(--text-secondary)] truncate">{quote.title}</p>
                )
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Vehicle + dates */}
          <div className="px-6 py-4 border-b border-[var(--border)] bg-[var(--bg-base)]">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[var(--text-muted)] text-xs mb-0.5">Vehicle</p>
                <p className="font-medium text-[var(--text-primary)]">
                  {quote.registration} — {quote.make} {quote.model}
                </p>
              </div>
              <div>
                <p className="text-[var(--text-muted)] text-xs mb-0.5">Valid until</p>
                {isGarageView && isDraft ? (
                  <input
                    type="date"
                    value={meta.validUntil}
                    onChange={e => setMeta(m => ({ ...m, validUntil: e.target.value }))}
                    className="w-full px-2 py-1 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                               rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                ) : (
                  <p className="font-medium text-[var(--text-primary)]">{fmtDate(quote.valid_until)}</p>
                )}
              </div>
              <div>
                <p className="text-[var(--text-muted)] text-xs mb-0.5">Created</p>
                <p className="text-[var(--text-primary)]">{fmtDate(quote.created_at)}</p>
              </div>
              {quote.approved_at && (
                <div>
                  <p className="text-[var(--text-muted)] text-xs mb-0.5">Approved</p>
                  <p className="text-green-400">{fmtDate(quote.approved_at)}</p>
                </div>
              )}
              {quote.rejected_at && (
                <div>
                  <p className="text-[var(--text-muted)] text-xs mb-0.5">Rejected</p>
                  <p className="text-red-400">{fmtDate(quote.rejected_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Line items */}
          <div className="px-6 py-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-3">
              Line Items
            </p>

            {items.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] italic">No items added yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[var(--text-muted)] text-xs border-b border-[var(--border)]">
                      <th className="text-left pb-2 font-medium">Description</th>
                      <th className="text-left pb-2 font-medium">Type</th>
                      <th className="text-right pb-2 font-medium">Qty</th>
                      <th className="text-right pb-2 font-medium">Unit</th>
                      <th className="text-right pb-2 font-medium">VAT</th>
                      <th className="text-right pb-2 font-medium">Total</th>
                      {isGarageView && isDraft && <th className="w-20" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {items.map(item => (
                      <tr key={item.id} className="text-[var(--text-primary)]">
                        <td className="py-2.5 pr-3">{item.description}</td>
                        <td className="py-2.5 pr-3 capitalize text-[var(--text-secondary)] text-xs">
                          {item.type}
                        </td>
                        <td className="py-2.5 text-right">{Number(item.quantity)}</td>
                        <td className="py-2.5 text-right">{fmtGBP(item.unit_price)}</td>
                        <td className="py-2.5 text-right text-[var(--text-muted)] text-xs">
                          {item.vat_rate}%
                        </td>
                        <td className="py-2.5 text-right font-medium">{fmtGBP(item.total)}</td>
                        {isGarageView && isDraft && (
                          <td className="py-2.5 pl-2">
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              disabled={deleteItem.isPending}
                              className="p-1 text-[var(--text-muted)] hover:text-red-400 rounded transition-colors disabled:opacity-50"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add item form — garage, draft only */}
            {isGarageView && isDraft && (
              <AddItemForm quoteId={quote.id} garageId={quote.garage_id} vehicleId={quote.vehicle_id} />
            )}

            {/* Totals */}
            {items.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-1 text-sm">
                <div className="flex justify-between text-[var(--text-secondary)]">
                  <span>Subtotal</span>
                  <span>{fmtGBP(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-[var(--text-secondary)]">
                  <span>VAT</span>
                  <span>{fmtGBP(totals.vat_amount)}</span>
                </div>
                <div className="flex justify-between font-semibold text-[var(--text-primary)] text-base pt-1">
                  <span>Total</span>
                  <span>{fmtGBP(totals.total)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {isGarageView && isDraft ? (
            <div className="px-6 pb-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={meta.title}
                  onChange={e => setMeta(m => ({ ...m, title: e.target.value }))}
                  placeholder="e.g. Full service and brake overhaul…"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Notes for Customer
                </label>
                <textarea
                  rows={2}
                  value={meta.customerNotes}
                  onChange={e => setMeta(m => ({ ...m, customerNotes: e.target.value }))}
                  placeholder="Visible to the customer on the quote…"
                  className={clsx(inputCls, 'resize-none')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Internal Notes
                </label>
                <textarea
                  rows={2}
                  value={meta.notes}
                  onChange={e => setMeta(m => ({ ...m, notes: e.target.value }))}
                  placeholder="Internal only — not shown to the customer…"
                  className={clsx(inputCls, 'resize-none')}
                />
              </div>
            </div>
          ) : (quote.customer_notes || (isGarageView && quote.notes)) ? (
            <div className="px-6 pb-4 space-y-3">
              {quote.customer_notes && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                    Notes for Customer
                  </p>
                  <p className="text-sm text-[var(--text-primary)] whitespace-pre-line">{quote.customer_notes}</p>
                </div>
              )}
              {isGarageView && quote.notes && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                    Internal Notes
                  </p>
                  <p className="text-sm text-[var(--text-primary)] whitespace-pre-line">{quote.notes}</p>
                </div>
              )}
            </div>
          ) : null}

          {/* Rejection reason */}
          {quote.rejection_reason && (
            <div className="px-6 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-400 mb-1">
                Rejection Reason
              </p>
              <p className="text-sm text-[var(--text-primary)]">{quote.rejection_reason}</p>
            </div>
          )}

          {/* Rejection input */}
          {rejecting && (
            <div className="px-6 pb-4">
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Reason for rejection <span className="text-[var(--text-muted)]">(optional)</span>
              </label>
              <textarea
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Let the garage know why you're declining…"
                className={clsx(inputCls, 'resize-none')}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => setRejecting(false)}
                  className="flex-1 py-2 bg-[var(--bg-overlay)] text-[var(--text-secondary)]
                             text-sm rounded-lg transition-colors hover:bg-[var(--bg-elevated)]"
                >
                  Back
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectQuote.isPending}
                  className="flex-1 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50
                             text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {rejectQuote.isPending ? 'Rejecting…' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-[var(--border)] shrink-0">
          {/* Garage actions */}
          {isGarageView && canAdmin && isDraft && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleDelete}
                disabled={deleteQuote.isPending}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-overlay)] hover:bg-red-500/10
                           hover:text-red-400 disabled:opacity-50 text-[var(--text-secondary)]
                           text-sm font-medium rounded-lg transition-colors border border-[var(--border)]"
              >
                <Trash2 size={15} />
                {deleteQuote.isPending ? 'Deleting…' : 'Delete'}
              </button>
              <div className="flex-1" />
              <button
                onClick={handleSave}
                disabled={updateQuote.isPending}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           disabled:opacity-50 text-[var(--text-secondary)] border border-[var(--border)]
                           text-sm font-medium rounded-lg transition-colors"
              >
                {updateQuote.isPending ? 'Saving…' : 'Save Draft'}
              </button>
              <button
                onClick={handleSend}
                disabled={sendQuote.isPending || items.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Send size={15} />
                {sendQuote.isPending ? 'Sending…' : 'Send to Customer'}
              </button>
            </div>
          )}
          {isGarageView && canAdmin && isDraft && items.length === 0 && (
            <p className="mt-1 text-xs text-[var(--text-muted)]">Add at least one line item before sending.</p>
          )}

          {/* Customer actions */}
          {!isGarageView && isActionable && !rejecting && (
            <div className="flex gap-3">
              <button
                onClick={() => setRejecting(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors border border-[var(--border)]"
              >
                <Ban size={15} />
                Decline
              </button>
              <button
                onClick={handleApprove}
                disabled={approveQuote.isPending}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Check size={15} />
                {approveQuote.isPending ? 'Approving…' : 'Approve Quote'}
              </button>
            </div>
          )}

          {/* Status info for non-actionable states */}
          {!isGarageView && !isActionable && !rejecting && (
            <p className="text-sm text-[var(--text-secondary)]">
              {quote.status === 'approved' && 'You approved this quote.'}
              {quote.status === 'rejected' && 'You declined this quote.'}
              {quote.status === 'draft'    && 'This quote hasn\'t been sent to you yet.'}
              {quote.status === 'expired'  && 'This quote has expired.'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Quote card ───────────────────────────────────────────────────────────────

function QuoteCard({ quote, isGarageView, canAdmin, onOpen, onDelete }) {
  const needsAction = !isGarageView && ['sent', 'viewed'].includes(quote.status)
  const canDelete   = canAdmin && quote.status === 'draft'

  return (
    <div
      className={clsx(
        'flex items-stretch bg-[var(--bg-surface)] border rounded-xl transition-colors overflow-hidden',
        needsAction
          ? 'border-indigo-500/40 hover:border-indigo-500/70'
          : 'border-[var(--border)] hover:border-[var(--border-hover,var(--border))]'
      )}
    >
      <button
        onClick={onOpen}
        className="flex-1 text-left p-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-mono text-sm font-semibold text-[var(--text-primary)]">
                {quote.quote_number}
              </p>
              <StatusBadge status={quote.status} />
              {needsAction && (
                <span className="text-xs text-indigo-400 font-medium">Action required</span>
              )}
            </div>
            {quote.title && (
              <p className="text-sm font-medium text-[var(--text-primary)] truncate mb-0.5">
                {quote.title}
              </p>
            )}
            <p className="text-sm text-[var(--text-secondary)]">
              {quote.registration} — {quote.make} {quote.model}
            </p>
            {quote.valid_until && (
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Valid until {fmtDate(quote.valid_until)}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-lg font-bold text-[var(--text-primary)]">{fmtGBP(quote.total)}</p>
            <p className="text-xs text-[var(--text-muted)]">{fmtDate(quote.created_at)}</p>
          </div>
        </div>
      </button>

      {canDelete && (
        <button
          onClick={onDelete}
          title="Delete draft quote"
          className="px-4 border-l border-[var(--border)] text-[var(--text-muted)]
                     hover:text-red-400 hover:bg-red-500/5 transition-colors shrink-0"
        >
          <Trash2 size={15} />
        </button>
      )}
    </div>
  )
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────

const GARAGE_FILTERS  = ['all', 'draft', 'sent', 'viewed', 'approved', 'rejected', 'expired']
const CUSTOMER_FILTERS = ['all', 'pending', 'approved', 'rejected']

function FilterTabs({ filters, active, onChange }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {filters.map(f => (
        <button
          key={f}
          onClick={() => onChange(f)}
          className={clsx(
            'px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors',
            active === f
              ? 'bg-indigo-600 text-white'
              : 'bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
          )}
        >
          {f === 'all' ? 'All' : f === 'pending' ? 'Pending Approval' : STATUS[f]?.label || f}
        </button>
      ))}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function QuotesPage() {
  const { user }    = useAuth()
  const isGarageView = ['garage_user', 'garage_admin', 'platform_admin'].includes(user?.role)
  const canAdmin    = ['garage_admin', 'platform_admin'].includes(user?.role)
  const deleteQuote = useDeleteQuote()

  const filterOptions = isGarageView ? GARAGE_FILTERS : CUSTOMER_FILTERS
  const [filter,      setFilter]      = useState('all')
  const [showCreate,  setShowCreate]  = useState(false)
  const [detailId,    setDetailId]    = useState(null)

  const handleDeleteQuote = async (quote) => {
    if (!window.confirm(`Delete draft quote ${quote.quote_number}? This cannot be undone.`)) return
    try {
      await deleteQuote.mutateAsync(quote.id)
      toast.success('Quote deleted')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete quote')
    }
  }

  // Build API query params from filter
  const queryParams = (() => {
    if (filter === 'all')     return {}
    if (filter === 'pending') return { status: 'sent' }  // covers sent+viewed; we also pass viewed below
    return { status: filter }
  })()

  // For 'pending' we want both sent and viewed — fetch without status filter and filter client-side
  const { data: allQuotes = [], isLoading } = useQuotes(filter === 'pending' ? {} : queryParams)

  const quotes = filter === 'pending'
    ? allQuotes.filter(q => ['sent', 'viewed'].includes(q.status))
    : allQuotes

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Quotes</h1>
          {!isLoading && quotes.length > 0 && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {quotes.length} {quotes.length === 1 ? 'quote' : 'quotes'}
            </p>
          )}
        </div>
        {isGarageView && canAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            New Quote
          </button>
        )}
      </div>

      {/* Filters */}
      <FilterTabs filters={filterOptions} active={filter} onChange={setFilter} />

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : quotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
            <FileText size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">
            No quotes
          </h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            {isGarageView
              ? filter === 'all'
                ? 'Create your first quote to send to a customer.'
                : `No ${filter} quotes.`
              : filter === 'pending'
                ? 'No quotes awaiting your approval.'
                : 'No quotes to show.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map(q => (
            <QuoteCard
              key={q.id}
              quote={q}
              isGarageView={isGarageView}
              canAdmin={canAdmin}
              onOpen={() => setDetailId(q.id)}
              onDelete={() => handleDeleteQuote(q)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateQuoteModal onClose={() => setShowCreate(false)} />
      )}
      {detailId && (
        <QuoteDetailModal
          quoteId={detailId}
          isGarageView={isGarageView}
          canAdmin={canAdmin}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  )
}
